
import { supabase } from '@/integrations/supabase/client';
import { currentEnvironment } from '@/config/environment';

export interface CronJobConfig {
  name: string;
  schedule: string;
  functionName: string;
  enabled: boolean;
  environment: string;
}

export class CronJobManager {
  /**
   * Create or update an environment-specific cron job
   */
  static async createCronJob(jobConfig: CronJobConfig): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`Creating cron job: ${jobConfig.name} for environment: ${currentEnvironment}`);
      
      const { data, error } = await supabase.functions.invoke('manage-cron-jobs', {
        body: {
          action: 'create',
          jobConfig: {
            ...jobConfig,
            environment: currentEnvironment
          },
          environment: currentEnvironment
        }
      });

      if (error) {
        throw new Error(`Edge function error: ${error.message}`);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to create cron job');
      }

      console.log(`Successfully created cron job: ${jobConfig.name}`);
      return { success: true };
    } catch (error) {
      console.error('Error creating cron job:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Remove a cron job for the current environment
   */
  static async removeCronJob(jobName: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`Removing cron job: ${jobName} for environment: ${currentEnvironment}`);
      
      const { data, error } = await supabase.functions.invoke('manage-cron-jobs', {
        body: {
          action: 'remove',
          jobName,
          environment: currentEnvironment
        }
      });

      if (error) {
        throw new Error(`Edge function error: ${error.message}`);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to remove cron job');
      }

      console.log(`Successfully removed cron job: ${jobName}`);
      return { success: true };
    } catch (error) {
      console.error('Error removing cron job:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * List all cron jobs for the current environment
   */
  static async listCronJobs(): Promise<CronJobConfig[]> {
    try {
      console.log(`Listing cron jobs for environment: ${currentEnvironment}`);
      
      const { data, error } = await supabase.functions.invoke('manage-cron-jobs', {
        body: {
          action: 'list',
          environment: currentEnvironment
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to list cron jobs');
      }

      const jobs = data.jobs || [];
      console.log(`Found ${jobs.length} cron jobs for environment ${currentEnvironment}`);
      return jobs;
    } catch (error) {
      console.error('Error listing cron jobs:', error);
      return [];
    }
  }

  /**
   * Clean up cron jobs from other environments
   */
  static async cleanupCrossEnvironmentJobs(): Promise<{ success: boolean; cleaned: number }> {
    try {
      console.log(`Cleaning up cross-environment cron jobs for: ${currentEnvironment}`);
      
      const { data, error } = await supabase.functions.invoke('manage-cron-jobs', {
        body: {
          action: 'cleanup',
          environment: currentEnvironment
        }
      });

      if (error) {
        throw new Error(`Edge function error: ${error.message}`);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to cleanup cron jobs');
      }

      const cleaned = data.cleaned || 0;
      console.log(`Cleaned up ${cleaned} cross-environment cron jobs`);
      return { success: true, cleaned };
    } catch (error) {
      console.error('Error cleaning up cross-environment jobs:', error);
      return { success: false, cleaned: 0 };
    }
  }

  /**
   * Initialize environment with required cron jobs
   */
  static async initializeEnvironmentJobs(): Promise<{ success: boolean; created: number; errors: string[] }> {
    try {
      console.log(`Initializing environment jobs for: ${currentEnvironment}`);
      
      const { data, error } = await supabase.functions.invoke('manage-cron-jobs', {
        body: {
          action: 'initialize',
          environment: currentEnvironment
        }
      });

      if (error) {
        throw new Error(`Edge function error: ${error.message}`);
      }

      const result = {
        success: data?.success || false,
        created: data?.created || 0,
        errors: data?.errors || []
      };

      console.log(`Environment initialization completed: ${result.created} jobs created, ${result.errors.length} errors`);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error initializing environment jobs:', errorMessage);
      return {
        success: false,
        created: 0,
        errors: [errorMessage]
      };
    }
  }
}
